import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function ListingDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showApply, setShowApply] = useState(false);
  const [letter, setLetter] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [shareFlash, setShareFlash] = useState("");

  useEffect(() => {
    api
      .get(`/listings/${id}`)
      .then((r) => setListing(r.data))
      .finally(() => setLoading(false));
  }, [id]);

  const shareText = () => {
    if (!listing) return "";
    const kind = listing.listing_type === "hermitage" ? "" : " — seeking a hearth";
    return `${listing.title}${kind}: ${listing.summary} · Hearth & Hermit`;
  };

  const shareUrl = () => `${window.location.origin}/listings/${id}`;

  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText()}\n${shareUrl()}`);
      setShareFlash("Copied. Now paste it where the right people will see it.");
      setTimeout(() => setShareFlash(""), 3000);
    } catch {
      setShareFlash("Could not copy. Long-press the URL above.");
    }
  };

  const tweetHref = () => {
    const text = encodeURIComponent(shareText());
    const url = encodeURIComponent(shareUrl());
    return `https://twitter.com/intent/tweet?text=${text}&url=${url}`;
  };

  const submitLetter = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await api.post("/applications", { listing_id: id, letter });
      setSuccess(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <p className="text-center py-24 marginalia">Reading the parchment…</p>;
  if (!listing) return <p className="text-center py-24 marginalia">This listing has been withdrawn.</p>;

  const isOwner = user && listing.owner_id === user.id;

  return (
    <main className="max-w-5xl mx-auto px-6 sm:px-10 py-12">
      <Link to={`/browse/${listing.listing_type === "hermitage" ? "hermitages" : "hermits"}`} className="label-overline hover:text-wax-red">← back to listings</Link>

      <div className="grid grid-cols-12 gap-8 mt-6">
        <div className="col-span-12 md:col-span-7">
          {listing.image_url && (
            <img src={listing.image_url} alt={listing.title} className="w-full h-[420px] object-cover border border-rule mb-6" />
          )}
          <p className="label-overline">{listing.region} · {listing.listing_type === "hermitage" ? "Hermitage" : "Hermit offer"}</p>
          <h1 className="font-display text-5xl mt-2">{listing.title}</h1>
          <p className="font-body italic text-xl text-ink-iron mt-3">{listing.summary}</p>
          <div className="editorial-rule my-6" />
          <div className="font-body text-ink-iron whitespace-pre-wrap leading-relaxed">{listing.description}</div>
          {listing.terms && (
            <>
              <h3 className="font-display text-2xl mt-10">Terms of the arrangement</h3>
              <p className="font-body text-ink-iron whitespace-pre-wrap mt-3">{listing.terms}</p>
            </>
          )}
        </div>

        <aside className="col-span-12 md:col-span-5">
          <div className="parchment-card sticky top-24">
            <p className="label-overline">Posted by</p>
            <p className="font-display text-2xl">{listing.owner_name}</p>
            <div className="editorial-rule my-4" />
            <dl className="space-y-3 text-sm font-body">
              <div><dt className="label-overline">Location</dt><dd className="mt-1">{listing.location}</dd></div>
              <div><dt className="label-overline">Duration</dt><dd className="mt-1">{listing.duration}</dd></div>
            </dl>

            {!isOwner && (
              <div className="mt-6 space-y-3">
                {user ? (
                  <>
                    <button data-testid="apply-open-btn" onClick={() => setShowApply(true)} className="wax-button w-full">Submit Letter of Intent</button>
                    <button data-testid="message-owner-btn" onClick={() => navigate(`/messages/${listing.owner_id}?listing=${listing.listing_id}`)} className="ghost-button w-full">Send a private letter</button>
                  </>
                ) : (
                  <Link to="/sign-up" className="wax-button w-full inline-block text-center">Sign up to apply</Link>
                )}
              </div>
            )}
            {isOwner && <p className="mt-6 marginalia">This is your listing.</p>}

            {/* Share — visible to everyone, owner included */}
            <div className="mt-6 pt-6 border-t border-rule" data-testid="share-block">
              <p className="label-overline mb-3">Share this listing</p>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={copyShare}
                  data-testid="share-copy-btn"
                  className="ghost-button text-xs py-2"
                >Copy link &amp; pitch</button>
                <a
                  href={tweetHref()}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="share-tweet-btn"
                  className="ghost-button text-xs py-2 text-center"
                >Post on X</a>
              </div>
              {shareFlash && <p className="font-body italic text-moss text-xs mt-2">{shareFlash}</p>}
            </div>
          </div>
        </aside>
      </div>

      {/* Apply modal */}
      {showApply && (
        <div className="fixed inset-0 bg-ink-black/50 z-50 flex items-center justify-center p-6" onClick={() => setShowApply(false)}>
          <div className="parchment-card max-w-xl w-full" onClick={(e) => e.stopPropagation()}>
            {success ? (
              <div className="text-center" data-testid="apply-success">
                <div className="fleuron mb-4">❦</div>
                <h3 className="font-display text-3xl">Your letter is sealed.</h3>
                <p className="font-body text-ink-iron mt-3">It awaits the host.</p>
                <button onClick={() => navigate("/dashboard")} className="wax-button mt-6">To dashboard</button>
              </div>
            ) : (
              <form onSubmit={submitLetter} data-testid="apply-form">
                <p className="label-overline">A letter of intent</p>
                <h3 className="font-display text-3xl mt-2 mb-2">To the steward of <em className="text-wax-red">{listing.title}</em></h3>
                <p className="marginalia mb-4">Speak honestly. State your skills, your silences, and your reasons.</p>
                <textarea
                  data-testid="apply-letter"
                  className="field"
                  rows={8}
                  required
                  minLength={20}
                  value={letter}
                  onChange={(e) => setLetter(e.target.value)}
                  placeholder="Most honourable host, I write to offer my presence on your land…"
                />
                {error && <p className="text-wax-red text-sm italic mt-2">{error}</p>}
                <div className="flex gap-3 mt-4">
                  <button type="button" onClick={() => setShowApply(false)} className="ghost-button flex-1">Cancel</button>
                  <button data-testid="apply-submit" disabled={submitting} className="wax-button flex-1">{submitting ? "Sealing…" : "Seal & send"}</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </main>
  );
}
